import React from 'react';
import classNames from 'classnames';
import { Card } from 'react-bootstrap';
import { ChartData, ChartOptions } from 'chart.js';
import Doughnut from '../../../../../components/charts/Doughnut';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { Theme } from '../../../../../theme';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { BudgetOverview } from '../types';
import { ReviewManagerLink } from './ReviewManagerLink';

type Props = {
  overview: BudgetOverview;
  assessingManagerName: string;
  buildDetailUrl: (reviewId: string) => string;
  // Stretch the card to the full column height. The donut is the sidebar's sole
  // card, so it fills the column to align with the taller main column.
  fillHeight?: boolean;
};

const CHART_OPTIONS: ChartOptions<'doughnut'> = {
  cutout: '72%',
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
};

// Budget overview donut: the review's own consumed vs available, with the
// funding pool and the access-aware "reports to" pointer. Rendered only when
// the review has a budget.
export const BudgetOverviewCard: React.FC<Props> = ({
  overview,
  assessingManagerName,
  buildDetailUrl,
  fillHeight = false,
}) => {
  const t = useNamespacedTranslation('compensationReviews.budget');
  const { available, consumed, remaining, consumedPercent, overBudget } =
    overview;

  // Filled vs remaining ring. Over budget → a full red ring; otherwise the
  // consumed slice (green) against the remaining track (grey).
  const hasPositiveBudget = available !== null && available > 0;
  const filled = hasPositiveBudget ? Math.min(consumed, available) : consumed;
  const rest = hasPositiveBudget ? Math.max(0, available - consumed) : 0;
  // A zero budget fully unconsumed leaves both slices at 0, which Chart.js
  // renders as an invisible ring. A zero budget is a real state (distinct from
  // "no budget"), so fall back to a full grey track so the ring stays visible.
  const isEmpty = filled === 0 && rest === 0;

  const data: ChartData<'doughnut'> = {
    labels: [t('consumed'), t('remaining')],
    datasets: [
      {
        data: isEmpty ? [0, 1] : [filled, rest],
        backgroundColor: [
          overBudget ? Theme.colorHex.red1 : Theme.colorHex.teal1,
          Theme.colorHex.gray4,
        ],
        borderWidth: 0,
      },
    ],
  };

  return (
    <Card className={fillHeight ? 'h-100' : undefined}>
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FlairIcon icon="phosphor-scales" className="me-2" />
          <h4 className="mb-0">{t('overviewTitle')}</h4>
        </div>

        <div className="budget-overview__donut mx-auto mb-3">
          <Doughnut data={data} options={CHART_OPTIONS} height="10rem">
            <span
              className={classNames('budget-overview__donut-label', {
                'text-danger': overBudget,
              })}>
              {consumedPercent !== null ? `${consumedPercent}%` : '—'}
            </span>
          </Doughnut>
        </div>

        <dl className="row mb-0">
          <dt className="col-6 text-muted fw-normal">{t('consumed')}</dt>
          <dd className="col-6 text-end fw-bold">
            <FormattedCurrency value={consumed} currency={overview.currency} />
          </dd>

          {available !== null ? (
            <>
              <dt className="col-6 text-muted fw-normal">{t('available')}</dt>
              <dd className="col-6 text-end fw-bold">
                <FormattedCurrency
                  value={available}
                  currency={overview.currency}
                />
              </dd>
              <dt className="col-6 text-muted fw-normal">{t('remaining')}</dt>
              <dd
                className={classNames('col-6 text-end fw-bold', {
                  'text-danger': remaining !== null && remaining < 0,
                })}>
                {remaining !== null && (
                  <FormattedCurrency
                    value={remaining}
                    currency={overview.currency}
                  />
                )}
              </dd>
            </>
          ) : (
            <dd className="col-12 text-muted">{t('noBudgetSet')}</dd>
          )}
        </dl>

        <div className="text-muted small mt-2">{t('monthlyEquivalents')}</div>

        {overBudget && (
          <div className="text-danger small mt-2 d-flex align-items-center">
            <FlairIcon
              icon="alert-triangle-outline"
              size="sm"
              className="me-1"
            />
            {t('overBudgetWarning')}
          </div>
        )}

        <hr />

        <div className="d-flex justify-content-between small mb-2">
          <span className="text-muted">{t('assessingManager')}</span>
          <span className="fw-bold text-end">{assessingManagerName}</span>
        </div>
        {overview.reportsTo && (
          <div className="d-flex justify-content-between small mb-2">
            <span className="text-muted">{t('reportsTo')}</span>
            <span className="fw-bold text-end">
              <ReviewManagerLink
                reviewId={overview.reportsTo.reviewId}
                name={overview.reportsTo.managerName}
                accessible={overview.reportsTo.accessible}
                buildDetailUrl={buildDetailUrl}
              />
            </span>
          </div>
        )}
        {overview.masterBudgetName && (
          <div className="d-flex justify-content-between small">
            <span className="text-muted">{t('masterBudget')}</span>
            <span className="fw-bold text-end">
              {overview.masterBudgetName}
            </span>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
